import type { Theme } from '@emotion/react';
import { last } from 'lodash';

import type { AssignmentSubmission } from '@didaskill/domain/assignment-submissions/types';
import {
  ASSIGNMENT_ASSESSMENTS,
  ASSIGNMENT_GRADE_VARIANT,
  ASSIGNMENT_PERFORMANCE_THRESHOLDS,
  SUBMISSION_STATUSES,
} from '@didaskill/domain/assignments/constants';
import dayjs from '@didaskill/services/dayjs';
import { type TAG_VARIANTS, Tag } from '@didaskill/ui/design-system';

/**
 * Define the color to display for a submission based on urgency to correct it.
 *
 * @param submission - Assignment submission.
 * @param theme - Theme of eversity.
 * @returns - Color.
 */
export const getSubmissionColorFromTheme = (submission: AssignmentSubmission, theme: Theme) => {
  const { status } = submission;

  if (status === SUBMISSION_STATUSES.CORRECTED) {
    return theme.colors.gray[200];
  }

  const lastDeposit = last(submission.deposits);
  const submissionDaysFromNow = dayjs().diff(dayjs(lastDeposit.submissionDate), 'day', true);

  if (submissionDaysFromNow < 1) {
    return theme.colors.success[500];
  }

  if (submissionDaysFromNow < 7) {
    return theme.colors.warning[500];
  }

  return theme.colors.danger[500];
};

export const getGradeLevel = (
  score?: number,
  assessment?: ASSIGNMENT_ASSESSMENTS,
): ASSIGNMENT_GRADE_VARIANT => {
  if (
    score > ASSIGNMENT_PERFORMANCE_THRESHOLDS.GOOD ||
    [ASSIGNMENT_ASSESSMENTS.SATISFACTORY, ASSIGNMENT_ASSESSMENTS.VERY_SATISFACTORY].includes(
      assessment,
    )
  ) {
    return ASSIGNMENT_GRADE_VARIANT.SUCCESS;
  }

  if (
    score > ASSIGNMENT_PERFORMANCE_THRESHOLDS.POOR ||
    assessment === ASSIGNMENT_ASSESSMENTS.UNSATISFACTORY
  ) {
    return ASSIGNMENT_GRADE_VARIANT.WARNING;
  }

  if (
    (score && score < ASSIGNMENT_PERFORMANCE_THRESHOLDS.POOR) ||
    assessment === ASSIGNMENT_ASSESSMENTS.VERY_UNSATISFACTORY
  ) {
    return ASSIGNMENT_GRADE_VARIANT.DANGER;
  }

  return ASSIGNMENT_GRADE_VARIANT.DEFAULT;
};

export const getSubmissionGradeColor = (
  theme: Theme,
  score?: number,
  assessment?: ASSIGNMENT_ASSESSMENTS,
) => {
  const level = getGradeLevel(score, assessment);

  const map = {
    [ASSIGNMENT_GRADE_VARIANT.SUCCESS]: theme.colors.success[500],
    [ASSIGNMENT_GRADE_VARIANT.WARNING]: theme.colors.warning[500],
    [ASSIGNMENT_GRADE_VARIANT.DANGER]: theme.colors.danger[500],
    [ASSIGNMENT_GRADE_VARIANT.DEFAULT]: theme.colors.gray[500],
  };

  return map[level];
};

export const getAssignmentCardGradeColor = (
  theme: Theme,
  score?: number,
  assessment?: ASSIGNMENT_ASSESSMENTS,
) => {
  const level = getGradeLevel(score, assessment);

  const map = {
    [ASSIGNMENT_GRADE_VARIANT.SUCCESS]: theme.colors.success[25],
    [ASSIGNMENT_GRADE_VARIANT.WARNING]: theme.colors.warning[25],
    [ASSIGNMENT_GRADE_VARIANT.DANGER]: theme.colors.danger[25],
    [ASSIGNMENT_GRADE_VARIANT.DEFAULT]: theme.colors.gray[0],
  };

  return map[level];
};

export const getSubmissionGradeBadgeVariant = (
  score?: number,
  assessment?: ASSIGNMENT_ASSESSMENTS,
): TAG_VARIANTS => {
  const level = getGradeLevel(score, assessment);

  const map = {
    [ASSIGNMENT_GRADE_VARIANT.SUCCESS]: Tag.VARIANTS.SUCCESS,
    [ASSIGNMENT_GRADE_VARIANT.WARNING]: Tag.VARIANTS.WARNING,
    [ASSIGNMENT_GRADE_VARIANT.DANGER]: Tag.VARIANTS.DANGER,
    [ASSIGNMENT_GRADE_VARIANT.DEFAULT]: Tag.VARIANTS.PRIMARY,
  };

  return map[level];
};
